import AuthProvider from '../auth/provider';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import moment from 'moment';

import sharedContext from '../sharedContext';

const list2map = (list, key) => {
    const m = {};
    list.forEach((e) => {
        m[e[key]] = e;
    });
    return m;
};

const getInitialState = () => {
    const bootstrap = sharedContext.get('bootstrap');
    const is_authenticated = bootstrap && bootstrap.token && bootstrap.user && bootstrap.tour_operator && bootstrap.settings;
    return {
        loading: false,
        is_authenticated,
        countryMap: bootstrap && bootstrap.countries ? list2map(bootstrap.countries, 'id') : null,
        currencyMap: bootstrap && bootstrap.currencies ? list2map(bootstrap.currencies, 'id') : null,
        roleMap: bootstrap && bootstrap.roles ? list2map(bootstrap.roles, 'id') : null,
        timeline_types: bootstrap && bootstrap.timeline_types ? list2map(bootstrap.timeline_types, 'id') : null,
        ...bootstrap,
    };
};

const getEmptyState = () => {
    return {
        is_authenticated: false,
        token: null,
        token_expires: null,
        user: null,
        tour_operator: null,
        settings: null,
        countries: null,
        currencies: null,
        roles: null,
        countryMap: null,
        currencyMap: null,
        roleMap: null,
        timeline_types: null,
    };
};

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.
export const login = createAsyncThunk('auth/login', async (loginInfo) => await AuthProvider.login(loginInfo));

export const rotateToken = createAsyncThunk('auth/rotateToken', async (token) => await AuthProvider.rotateToken(token));

export const authSlice = createSlice({
    name: 'auth',
    initialState: getInitialState(),
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        logout: (state) => {
            //sharedContext.get('twilioClient')?.shutdown()
            sharedContext.delete('bootstrap');
            sharedContext.delete('twilioClientIsInitializing');
            sharedContext.delete('twilioClient');
            sharedContext.delete('unreadMsgCount');

            sharedContext.clear(); // added by Jordan 9/27/2022 to ensure all chat related keys are deleted as well  is this OK? Doesn't seem to actually clear anything

            state = getEmptyState();

            if (window.navigator && navigator.serviceWorker) {
                navigator.serviceWorker.getRegistrations().then(function (registrations) {
                    for (let registration of registrations) {
                        registration.unregister();
                    }
                });
            }

            return state;
        },
        updateTourOperator: (state, action) => {
            state.tour_operator = action.payload;
            const current = sharedContext.get('bootstrap');
            current.tour_operator = action.payload;
            sharedContext.set('bootstrap', current);
            return state;
        },
    },
    // The `extraReducers` field lets the slice handle actions defined elsewhere,
    // including actions generated by createAsyncThunk or in other slices.
    extraReducers: (builder) => {
        builder
            .addCase(login.pending, (state) => {
                console.log('login pending', state);
                state.loading = true;
            })
            .addCase(login.fulfilled, (state, action) => {
                state.loading = false;
                state.is_authenticated = true;
                state.token = action.payload.token;
                state.token_expires = action.payload.token_expires;
                state.user = action.payload.user;
                state.tour_operator = action.payload.tour_operator;
                state.settings = action.payload.settings;
                state.countries = action.payload.countries;
                state.currencies = action.payload.currencies;
                state.roles = action.payload.roles;
                state.countryMap = action.payload.countries && action.payload.countries.length > 0 ? list2map(action.payload.countries, 'id') : {};
                state.currencyMap = action.payload.currencies && action.payload.currencies.length > 0 ? list2map(action.payload.currencies, 'id') : {};
                state.roleMap = action.payload.roles && action.payload.roles.length > 0 ? list2map(action.payload.roles, 'id') : {};
                state.timeline_types = action.payload.timeline_types && action.payload.timeline_types.length > 0 ? list2map(action.payload.timeline_types, 'id') : {};
                sharedContext.set('bootstrap', action.payload);
                window.location.reload();
            })
            .addCase(login.rejected, (state, action) => {
                console.log('login rejected', state, action);
                state.loading = false;
                state.error = true;
                state.message = 'Login failed. Please check your username and/or password.';
            })
            .addCase(rotateToken.fulfilled, (state, action) => {
                state.token = action.payload.token;
                state.token_expires = action.payload.token_expires;
            })
            .addCase(rotateToken.rejected, (state, action) => {
                sharedContext.delete('bootstrap');
                state = getEmptyState();
                return state;
            });
    },
});

export const { logout, updateTourOperator } = authSlice.actions;

export const selectLoading = (state) => state.auth.loading;
export const selectIsAuthenticated = (state) => {
    if (!state.auth.is_authenticated) {
        return false;
    }
    const expires = state.auth.token_expires;
    if (!expires) {
        return false;
    }
    const now = moment();
    const exp = moment(expires);
    console.log('selectIsAuthenticated', exp.diff(now) > 0, exp.utc().format(), now.utc().format());
    return exp.diff(now) > 0;
};
export const selectUser = (state) => state.auth.user;
export const selectTourOperator = (state) => state.auth.tour_operator;
export const selectSettings = (state) => state.auth.settings;
export const selectCountries = (state) => state.auth.countries;
export const selectCurrencies = (state) => state.auth.currencies;
export const selectRoles = (state) => state.auth.roles;
export const selectTimelineTypes = (state) => state.auth.timeline_types;
export const selectCountryMap = (state) => state.auth.countryMap;
export const selectCurrencyMap = (state) => state.auth.currencyMap;
export const selectRoleMap = (state) => state.auth.roleMap;
export const selectTokenExpires = (state) => state.auth.token_expires;
export const selectToken = (state) => state.auth.token;
export const selectError = (state) => state.auth.error;
export const selectErrorMessage = (state) => state.auth.message;
export const selectTokenRefreshDelay = (state) => {
    const expires = state.auth.token_expires;
    if (!expires) {
        return 1000; // refresh token in a second if token expiration time is unknown
    }
    const now = moment();
    const exp = moment(expires);
    const refreshTime = exp.subtract(90, 'seconds');
    const delay = refreshTime.diff(now);
    if (!delay || delay <= 0) {
        return 30000;
    } else {
        return delay;
    }
};

export default authSlice.reducer;
